import * as instrument from "../../../../assessments/instruments/sample-cans/comprehensive-1-0-r2.js";
import {
  need,
  needOrNotApplicable,
} from "../../../../assessments/instruments/sample-cans/responseSets.js";
import type { Decorator } from "../../decorator.js";
import { commentRequiredForAnswers } from "../../decorators/commentRequiredForAnswers.js";
import { commentRequiredWhen } from "../../decorators/commentRequiredWhen.js";
import { formOrder } from "../../decorators/formOrder.js";
import { defineRule } from "../../rules.js";
import type { SupportedVersions } from "../../supportedVersions.js";

const { groups, items } = instrument;

/** The order the instrument module lists, which a stored schema does not keep. */
export const order: Decorator = formOrder(instrument, {
  groups: [
    groups.life_functioning,
    groups.behavioural_emotional_needs,
    groups.risk_behaviours,
    groups.cultural_factors,
    groups.strengths,
    groups.caregiver_resources_and_needs,
    groups.adverse_childhood_experiences,
  ],
  items: [
    items.family_functioning,
    items.living_situation,
    items.social_functioning,
    items.developmental_intellectual,
    items.decision_making,
    items.school_behaviour,
    items.school_achievement,
    items.school_attendance,
    items.medical_physical,
    items.sexual_development,
    items.sleep,

    items.psychosis,
    items.impulsivity_hyperactivity,
    items.depression,
    items.anxiety,
    items.oppositional_behaviour,
    items.conduct,
    items.adjustment_to_trauma,
    items.anger_control,
    items.substance_use,

    items.suicide_risk,
    items.non_suicidal_self_injury,
    items.other_self_harm,
    items.danger_to_others,
    items.sexual_aggression,
    items.runaway,
    items.delinquent_behaviour,
    items.intentional_misbehaviour,

    items.language,
    items.traditions_and_rituals,
    items.cultural_stress,

    items.family_strengths,
    items.interpersonal,
    items.educational_setting,
    items.talents_and_interests,
    items.spiritual_religious,
    items.cultural_identity,
    items.community_life,
    items.natural_supports,
    items.resilience,

    items.caregiver_supervision,
    items.caregiver_involvement_with_care,
    items.caregiver_knowledge,
    items.caregiver_organisation,
    items.caregiver_social_resources,
    items.caregiver_residential_stability,
    items.caregiver_medical_physical,
    items.caregiver_mental_health,
    items.caregiver_substance_use,
    items.caregiver_developmental,
    items.caregiver_safety,

    items.sexual_abuse,
    items.physical_abuse,
    items.emotional_abuse,
    items.neglect,
    items.medical_trauma,
    items.witness_to_family_violence,
    items.witness_to_community_violence,
    items.natural_or_man_made_disaster,
    items.war_or_terrorism_affected,
    items.victim_or_witness_to_criminal_activity,
    items.disrupted_caregiving,
    items.parental_criminal_behaviour,
  ],
});

/**
 * A need rated for action, at 2 or 3, is explained in a comment; below that,
 * a comment is welcome but not required. A school item rated not applicable needs
 * none.
 */
export const commentOnActionableNeeds: readonly Decorator[] = [
  commentRequiredForAnswers(need, [2, 3]),
  commentRequiredForAnswers(needOrNotApplicable, [2, 3]),
];

/**
 * Any concern about suicide risk is explained, even one only being watched.
 * Placed after the rule for every need, which it overrides for this item.
 */
export const commentOnAnySuicideRisk: Decorator = commentRequiredWhen(
  instrument,
  items.suicide_risk,
  defineRule(
    "suicide risk rated 1 or more",
    ({ answers }) => (answers.suicide_risk ?? 0) >= 1,
  ),
);

export const sampleCansComprehensive_1_0: SupportedVersions = {
  versions: [["sample_cans", "comprehensive", "1.0"]],
  decorators: [order, ...commentOnActionableNeeds, commentOnAnySuicideRisk],
};
